import * as React from "react";
import {
  HTMLTable,
  Button,
  Menu,
  MenuItem,
  Popover,
  Spinner,
  NonIdealState,
  Dialog,
  FormGroup,
  InputGroup,
  type OverlayToaster,
  Icon,
} from "@blueprintjs/core";
import { css } from "@emotion/core";
import ApiAdmin from "../../../api/admin";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { UserMultiSelect } from "../common/UserMultiSelect";
import { ConfirmationDialog } from "../common/ConfirmationDialog";
import {
  STRIPED_ROW_BACKGROUND,
  useAdminTableVirtualizer,
  VirtualizedTableBody,
} from "../common/VirtualizedTableBody";
import { useOutletContext, useParams } from "react-router-dom";
import { useAuthState } from "../../../context/auth";
import { isOrgAdminUser } from "../adminRoles";

const GROUP_TABLE_COLUMN_COUNT = 3;

function EditGroupDialog(props: {
  editDialogGroup: { id: number; name: string };
  onClose: () => void;
  organizationId: number;
}) {
  const { editDialogGroup, onClose } = props;

  const [editErrors, setEditErrors] = React.useState<string[]>([]);

  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const editGroupMutation = useMutation({
    mutationFn: ({ groupId, name }: { groupId: number; name: string }) =>
      ApiAdmin.updateGroup(props.organizationId, groupId, { name }),
    onMutate: async ({ groupId, name }) => {
      await queryClient.cancelQueries({
        queryKey: ["groups", props.organizationId],
      });
      const previousGroups = queryClient.getQueryData([
        "groups",
        props.organizationId,
      ]);

      queryClient.setQueryData(["groups", props.organizationId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          groups: old.groups.map((group: any) =>
            group.id === groupId ? { ...group, name } : group
          ),
        };
      });

      return { previousGroups };
    },
    onError: (err, { groupId }, context) => {
      if (context?.previousGroups) {
        queryClient.setQueryData(
          ["groups", props.organizationId],
          context.previousGroups
        );
      }
      if (toaster.current) {
        toaster.current.show({
          message: err.message || "Failed to update group. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: () => {
      if (toaster.current) {
        toaster.current.show({
          message: "Group updated successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      onClose();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["groups", props.organizationId],
      });
    },
  });

  const handleEditSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors: string[] = [];
    const nameInput = event.currentTarget.elements.namedItem(
      "edit-group-name"
    ) as HTMLInputElement;

    const name = nameInput?.value;

    if (!name || name.trim() === "") {
      errors.push("name");
    }

    if (errors.length === 0) {
      editGroupMutation.mutate({
        groupId: editDialogGroup.id,
        name: name.trim(),
      });
    } else {
      setEditErrors(errors);
    }
  };

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 524px;
      `}
      title="Edit Group"
      isOpen={true}
      onClose={() => {
        if (editGroupMutation.isPending === false) {
          onClose();
        }
      }}
    >
      <form
        css={css`
          padding: 1rem;
        `}
        onSubmit={handleEditSubmit}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <FormGroup
            label="Name"
            labelFor="edit-group-name"
            labelInfo="(required)"
            intent={editErrors.includes("name") ? "danger" : "none"}
          >
            <InputGroup
              id="edit-group-name"
              intent={editErrors.includes("name") ? "danger" : "none"}
              disabled={editGroupMutation.isPending}
              defaultValue={editDialogGroup.name}
              autoFocus
            />
          </FormGroup>
        </div>

        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding-top: 0.5rem;
          `}
        >
          <div
            css={css`
              color: var(--danger);
              flex-grow: 1;
            `}
          >
            {editErrors.length > 0 &&
              `Missing or invalid fields: ${editErrors.join(", ")}.`}
          </div>
          <div
            css={css`
              display: flex;
              justify-content: end;
              gap: 1rem;
            `}
          >
            <Button
              text="Cancel"
              disabled={editGroupMutation.isPending}
              intent="none"
              onClick={() => {
                if (editGroupMutation.isPending === false) {
                  onClose();
                }
              }}
            />
            <Button
              text={editGroupMutation.isPending ? "Saving..." : "Save"}
              disabled={editGroupMutation.isPending}
              intent="primary"
              type="submit"
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}

function AddGroupDialog(props: {
  onClose: () => void;
  organizationId: number;
}) {
  const { onClose } = props;

  const [addErrors, setAddErrors] = React.useState<string[]>([]);
  const [selectedUserIds, setSelectedUserIds] = React.useState<number[]>([]);

  const queryClient = useQueryClient();
  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const { data: usersData } = useQuery({
    queryKey: ["users", props.organizationId],
    queryFn: () => ApiAdmin.getUsers(props.organizationId),
  });

  const createGroupMutation = useMutation({
    mutationFn: ({ name, user_ids }: { name: string; user_ids: number[] }) =>
      ApiAdmin.createGroup(props.organizationId, { name, user_ids }),
    onMutate: async ({ name, user_ids }) => {
      await queryClient.cancelQueries({
        queryKey: ["groups", props.organizationId],
      });
      const previousGroups = queryClient.getQueryData([
        "groups",
        props.organizationId,
      ]);

      const tempId = Date.now();
      queryClient.setQueryData(["groups", props.organizationId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          groups: [
            ...old.groups,
            {
              id: tempId,
              name,
              user_ids,
            },
          ],
        };
      });

      return { previousGroups };
    },
    onError: (err, variables, context) => {
      if (context?.previousGroups) {
        queryClient.setQueryData(
          ["groups", props.organizationId],
          context.previousGroups
        );
      }
      if (toaster.current) {
        toaster.current.show({
          message: err.message || "Failed to create group. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: () => {
      if (toaster.current) {
        toaster.current.show({
          message: "Group created successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      onClose();
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["groups", props.organizationId],
      });
    },
  });

  const handleAddSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const errors: string[] = [];
    const nameInput = event.currentTarget.elements.namedItem(
      "add-group-name"
    ) as HTMLInputElement;

    const name = nameInput?.value;

    if (!name || name.trim() === "") {
      errors.push("name");
    }

    if (errors.length === 0) {
      createGroupMutation.mutate({
        name: name.trim(),
        user_ids: selectedUserIds,
      });
    } else {
      setAddErrors(errors);
    }
  };

  const handleUserChange = (userIds: number[]) => {
    setSelectedUserIds(userIds);
  };

  return (
    <Dialog
      className="bp6-dark"
      css={css`
        width: 524px;
      `}
      title="Add Group"
      isOpen={true}
      onClose={() => {
        if (createGroupMutation.isPending === false) {
          onClose();
        }
      }}
    >
      <form
        css={css`
          padding: 1rem;
        `}
        onSubmit={handleAddSubmit}
      >
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 1rem;
            margin-bottom: 1rem;
          `}
        >
          <FormGroup
            label="Name"
            labelFor="add-group-name"
            labelInfo="(required)"
            intent={addErrors.includes("name") ? "danger" : "none"}
          >
            <InputGroup
              id="add-group-name"
              intent={addErrors.includes("name") ? "danger" : "none"}
              disabled={createGroupMutation.isPending}
              autoFocus
            />
          </FormGroup>

          <FormGroup label="User Membership">
            <UserMultiSelect
              selectedUserIds={selectedUserIds}
              users={usersData?.users || []}
              onUserChange={handleUserChange}
            />
          </FormGroup>
        </div>

        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            width: 100%;
            padding-top: 0.5rem;
          `}
        >
          <div
            css={css`
              color: var(--danger);
              flex-grow: 1;
            `}
          >
            {addErrors.length > 0 &&
              `Missing or invalid fields: ${addErrors.join(", ")}.`}
          </div>
          <div
            css={css`
              display: flex;
              justify-content: end;
              gap: 1rem;
            `}
          >
            <Button
              text="Cancel"
              disabled={createGroupMutation.isPending}
              intent="none"
              onClick={() => {
                if (createGroupMutation.isPending === false) {
                  onClose();
                }
              }}
            />
            <Button
              text={createGroupMutation.isPending ? "Creating..." : "Create"}
              disabled={createGroupMutation.isPending}
              intent="primary"
              type="submit"
            />
          </div>
        </div>
      </form>
    </Dialog>
  );
}

function AddGroupButton(props: { organizationId: number }) {
  const [addDialogOpen, setAddDialogOpen] = React.useState(false);

  return (
    <>
      <Button
        icon="add-to-artifact"
        text="Add Group"
        intent="primary"
        onClick={() => setAddDialogOpen(true)}
      />
      {addDialogOpen && (
        <AddGroupDialog
          onClose={() => setAddDialogOpen(false)}
          organizationId={props.organizationId}
        />
      )}
    </>
  );
}

function GroupRow(props: {
  group: {
    id: number;
    name: string;
    user_ids: number[];
    autogenerated?: boolean;
  };
  index: number;
  users: any[];
  onUserChange: (groupId: number, userIds: number[]) => void;
  onEditGroup: (groupId: number) => void;
  onDeleteGroup: (groupId: number) => void;
  measureRef: (node: HTMLTableRowElement | null) => void;
}) {
  const {
    group,
    index,
    users,
    onUserChange,
    onEditGroup,
    onDeleteGroup,
    measureRef,
  } = props;

  return (
    <tr
      ref={measureRef}
      data-index={index}
      css={
        index % 2 === 0
          ? css`
              td {
                background: ${STRIPED_ROW_BACKGROUND};
              }
            `
          : undefined
      }
    >
      <td>
        <div
          css={css`
            height: 30px;
            display: flex;
            align-items: center;
            padding-left: 5px;
          `}
        >
          {group.name}
        </div>
      </td>
      <td>
        <UserMultiSelect
          selectedUserIds={group.user_ids}
          users={users}
          onUserChange={(userIds) => onUserChange(group.id, userIds)}
        />
      </td>
      <td>
        {group.autogenerated !== true && (
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              height: 30px;
              width: 100%;
            `}
          >
            <Popover
              content={
                <Menu>
                  <MenuItem
                    icon="edit"
                    text="Edit"
                    onClick={() => onEditGroup(group.id)}
                  />
                  <MenuItem
                    icon="trash"
                    text="Delete"
                    onClick={() => onDeleteGroup(group.id)}
                    intent="danger"
                  />
                </Menu>
              }
              placement="bottom-end"
            >
              <Button size="small" icon="more" />
            </Popover>
          </div>
        )}
      </td>
    </tr>
  );
}

export default function AdminOrganizationGroups() {
  const { organization_id } = useParams<{ organization_id: string }>();
  const organizationId = Number(organization_id);

  const { toaster } = useOutletContext<{
    toaster: React.RefObject<OverlayToaster>;
  }>();

  const queryClient = useQueryClient();

  const [editDialogGroup, setEditDialogGroup] = React.useState<{
    id: number;
    name: string;
  } | null>(null);

  const [deleteDialogGroup, setDeleteDialogGroup] = React.useState<{
    id: number;
    name: string;
  } | null>(null);

  const [filterText, setFilterText] = React.useState("");
  const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">(
    "asc"
  );

  const { data, isLoading, error } = useQuery({
    queryKey: ["groups", organizationId],
    queryFn: () => ApiAdmin.getGroups(organizationId),
  });

  const { data: usersData } = useQuery({
    queryKey: ["users", organizationId],
    queryFn: () => ApiAdmin.getUsers(organizationId),
  });

  const users = React.useMemo(
    () => usersData?.users ?? [],
    [usersData]
  );

  const deleteGroupMutation = useMutation({
    mutationFn: (groupId: number) =>
      ApiAdmin.deleteGroup(organizationId, groupId),
    onMutate: async (groupId: number) => {
      await queryClient.cancelQueries({ queryKey: ["groups", organizationId] });
      await queryClient.cancelQueries({ queryKey: ["users", organizationId] });

      const previousGroups = queryClient.getQueryData([
        "groups",
        organizationId,
      ]);
      const previousUsers = queryClient.getQueryData(["users", organizationId]);

      queryClient.setQueryData(["groups", organizationId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          groups: old.groups.filter((group: any) => group.id !== groupId),
        };
      });

      queryClient.setQueryData(["users", organizationId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          users: old.users.map((user: any) => ({
            ...user,
            group_ids: user.group_ids.filter((id: number) => id !== groupId),
          })),
        };
      });

      return { previousGroups, previousUsers };
    },
    onError: (err, groupId, context) => {
      if (context?.previousGroups) {
        queryClient.setQueryData(
          ["groups", organizationId],
          context.previousGroups
        );
      }
      if (context?.previousUsers) {
        queryClient.setQueryData(
          ["users", organizationId],
          context.previousUsers
        );
      }
      if (toaster.current) {
        toaster.current.show({
          message: err.message || "Failed to delete group. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSuccess: () => {
      if (toaster.current) {
        toaster.current.show({
          message: "Group deleted successfully.",
          intent: "success",
          timeout: 3000,
        });
      }
      setDeleteDialogGroup(null);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["groups", organizationId] });
      queryClient.invalidateQueries({ queryKey: ["users", organizationId] });
    },
  });

  const handleEditGroup = (groupId: number) => {
    const group = data?.groups.find((g) => g.id === groupId);
    if (group) {
      if (group.autogenerated === true) {
        return;
      }
      setEditDialogGroup({ id: group.id, name: group.name });
    }
  };

  const handleDeleteGroup = (groupId: number) => {
    const group = data?.groups.find((g) => g.id === groupId);
    if (group) {
      setDeleteDialogGroup({ id: group.id, name: group.name });
    }
  };

  const handleUserChange = (groupId: number, userIds: number[]) => {
    updateGroupMutation.mutate({ groupId, userIds });
  };

  const groupsWithUserIds = React.useMemo(() => {
    if (!data?.groups || !usersData?.users) return [];

    return data.groups.map((group) => ({
      ...group,
      user_ids: usersData.users
        .filter((u) => u.group_ids.includes(group.id))
        .map((u) => u.id),
    }));
  }, [data?.groups, usersData?.users]);

  const updateGroupMutation = useMutation({
    mutationFn: ({
      groupId,
      userIds,
    }: {
      groupId: number;
      userIds: number[];
    }) => ApiAdmin.updateGroup(organizationId, groupId, { user_ids: userIds }),
    onMutate: async ({ groupId, userIds }) => {
      await queryClient.cancelQueries({ queryKey: ["groups", organizationId] });
      await queryClient.cancelQueries({ queryKey: ["users", organizationId] });

      const previousGroups = queryClient.getQueryData([
        "groups",
        organizationId,
      ]);
      const previousUsers = queryClient.getQueryData(["users", organizationId]);

      queryClient.setQueryData(["groups", organizationId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          groups: old.groups.map((group: any) =>
            group.id === groupId ? { ...group, user_ids: userIds } : group
          ),
        };
      });

      queryClient.setQueryData(["users", organizationId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          users: old.users.map((user: any) => {
            const wasInGroup = user.group_ids.includes(groupId);
            const shouldBeInGroup = userIds.includes(user.id);

            if (wasInGroup && !shouldBeInGroup) {
              return {
                ...user,
                group_ids: user.group_ids.filter(
                  (id: number) => id !== groupId
                ),
              };
            } else if (!wasInGroup && shouldBeInGroup) {
              return { ...user, group_ids: [...user.group_ids, groupId] };
            }
            return user;
          }),
        };
      });

      return { previousGroups, previousUsers };
    },
    onError: (err, { groupId }, context) => {
      if (context?.previousGroups) {
        queryClient.setQueryData(
          ["groups", organizationId],
          context.previousGroups
        );
      }
      if (context?.previousUsers) {
        queryClient.setQueryData(
          ["users", organizationId],
          context.previousUsers
        );
      }

      if (toaster.current) {
        toaster.current.show({
          message:
            err.message ||
            "Failed to update group membership. Please try again.",
          intent: "danger",
          timeout: 5000,
        });
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["groups", organizationId] });
      queryClient.invalidateQueries({ queryKey: ["users", organizationId] });
    },
  });

  const filteredGroupsWithUserIds = React.useMemo(() => {
    if (!groupsWithUserIds) return [];

    const filtered = groupsWithUserIds.filter((group) => {
      if (!filterText.trim()) return true;
      const searchText = filterText.toLowerCase();
      return group.name.toLowerCase().includes(searchText);
    });

    return filtered.sort((a, b) => {
      const comparison = a.name.localeCompare(b.name);
      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [groupsWithUserIds, filterText, sortDirection]);

  const { currentUser } = useAuthState();
  const isCurrentUserAdmin = isOrgAdminUser(currentUser);

  const tableScrollRef = React.useRef<HTMLDivElement>(null);
  const rowVirtualizer = useAdminTableVirtualizer(
    filteredGroupsWithUserIds,
    tableScrollRef
  );

  let content;
  if (isLoading) {
    content = (
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          height: 200px;
        `}
      >
        <Spinner />
      </div>
    );
  } else if (error) {
    content = (
      <NonIdealState
        icon="error"
        title="Error Loading Groups"
        description={error.message}
      />
    );
  } else if (data?.groups.length === 0) {
    content = (
      <NonIdealState
        icon="folder-close"
        title="No Groups Found"
        description="Get started by creating your first group."
        action={
          isCurrentUserAdmin ? (
            <AddGroupButton organizationId={organizationId} />
          ) : undefined
        }
      />
    );
  } else if (filteredGroupsWithUserIds.length === 0 && filterText.trim()) {
    content = (
      <NonIdealState
        icon="search"
        title="No Groups Match Filter"
        description={`No groups found matching "${filterText}".`}
      />
    );
  } else {
    content = (
      <HTMLTable
        interactive
        css={css`
          width: 100%;
          thead {
            position: sticky;
            top: 0;
            background-color: var(--neutral-6);
            z-index: 1;

            th {
              border-bottom: 1px solid var(--neutral-5);
            }
          }

          tbody tr:first-child td {
            box-shadow: none !important;
          }
        `}
      >
        <colgroup>
          <col width="30%" />
          <col />
          <col width="64px" />
        </colgroup>
        <thead>
          <tr>
            <th
              onClick={() =>
                setSortDirection(sortDirection === "asc" ? "desc" : "asc")
              }
              css={css`
                cursor: pointer;
                user-select: none;

                &:hover {
                  background-color: var(--background-dark-translucent);
                }
              `}
            >
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  justify-content: space-between;
                  padding-left: 5px;
                `}
              >
                <span>Name</span>
                <Icon
                  icon={sortDirection === "asc" ? "chevron-up" : "chevron-down"}
                />
              </div>
            </th>
            <th>User Membership</th>
            <th></th>
          </tr>
        </thead>
        <VirtualizedTableBody
          virtualizer={rowVirtualizer}
          colSpan={GROUP_TABLE_COLUMN_COUNT}
          renderRow={(virtualRow) => {
            const group = filteredGroupsWithUserIds[virtualRow.index];
            return (
              <GroupRow
                key={group.id}
                group={group}
                index={virtualRow.index}
                users={users}
                onUserChange={handleUserChange}
                onEditGroup={handleEditGroup}
                onDeleteGroup={handleDeleteGroup}
                measureRef={rowVirtualizer.measureElement}
              />
            );
          }}
        />
      </HTMLTable>
    );
  }

  return (
    <div
      css={css`
        background-color: var(--neutral-6);
        height: 100%;
        display: flex;
        flex-direction: column;
      `}
    >
      {!isLoading && !error && (data?.groups?.length ?? 0) > 0 && (
        <div
          css={css`
            padding: 1rem;
            display: flex;
            justify-content: space-between;
            align-items: center;
            background-color: var(--neutral-7);
            border-bottom: 1px solid var(--neutral-5);
          `}
        >
          <InputGroup
            leftIcon="search"
            placeholder="Filter groups..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
            css={css`
              width: 300px;
            `}
          />
          <AddGroupButton organizationId={organizationId} />
        </div>
      )}
      <div
        ref={tableScrollRef}
        css={css`
          flex: 1;
          overflow: auto;
        `}
      >
        {content}
      </div>
      {editDialogGroup && (
        <EditGroupDialog
          key={editDialogGroup.id}
          editDialogGroup={editDialogGroup}
          onClose={() => setEditDialogGroup(null)}
          organizationId={organizationId}
        />
      )}
      {deleteDialogGroup && (
        <ConfirmationDialog
          entityName={deleteDialogGroup.name}
          entityType="Group"
          message="This group will be deleted and users will be removed from it."
          isOpen={true}
          onClose={() => setDeleteDialogGroup(null)}
          onConfirm={() => deleteGroupMutation.mutate(deleteDialogGroup.id)}
          isPending={deleteGroupMutation.isPending}
        />
      )}
    </div>
  );
}
